import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Select,
  Button,
  Badge,
  Space,
  Typography,
  Spin,
  Alert,
} from 'antd';
import {
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useMisaJobs, useMisaQueueStats } from '../../hooks/useMisa';
import { useTenants } from '../../hooks';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

const JOB_LABELS: Record<string, string> = {
  'misa-invoice-sync': 'Ho\u00e1 \u0111\u01a1n \u0111i\u1ec7n t\u1eed',
  'misa-sa-order-sync': '\u0110\u01a1n b\u00e1n h\u00e0ng',
  'misa-account-object-sync': 'Kh\u00e1ch h\u00e0ng / NCC',
  'misa-customer-user-sync': '\u0110\u1ed3ng b\u1ed9 User',
  'misa-bank-account-sync': 'T\u00e0i kho\u1ea3n ng\u00e2n h\u00e0ng',
  'misa-inventory-balance-sync': 'T\u1ed3n kho',
  'misa-inventory-serial-sync': 'Serial s\u1ea3n ph\u1ea9m',
  'misa-inventory-serial-book-detail-sync': 'Serial (chi ti\u1ebft)',
  'misa-inventory-item-sync': 'Danh m\u1ee5c s\u1ea3n ph\u1ea9m',
  'misa-address-backfill': 'B\u1ed5 sung \u0111\u1ecba ch\u1ec9',
  'misa-bank-sync': 'Danh s\u00e1ch ng\u00e2n h\u00e0ng',
  'misa-stock-sync': 'Kho h\u00e0ng',
  'misa-orgunit-sync': 'Chi nh\u00e1nh',
  'misa-act-open-account-sync': 'T\u00e0i kho\u1ea3n k\u1ebf to\u00e1n',
  'misa-sync-alert': 'C\u1ea3nh b\u00e1o \u0111\u1ed3ng b\u1ed9',
};

const statusBadgeMap: Record<string, { status: 'success' | 'error' | 'processing' | 'default'; text: string }> = {
  success: { status: 'success', text: 'Success' },
  failed: { status: 'error', text: 'Failed' },
  running: { status: 'processing', text: 'Running' },
  never: { status: 'default', text: 'Never' },
};

export const MisaDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedTenantId, setSelectedTenantId] = useState<string>(searchParams.get('tenantId') || '');
  const { tenants } = useTenants({ limit: 100 });
  const { jobs, stats, loading, error, refetch } = useMisaJobs(selectedTenantId);
  const { stats: queueStats } = useMisaQueueStats();

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            MISA Sync Dashboard
          </Title>
        </Col>
        <Col>
          <Space>
            <Select
              placeholder="Select tenant"
              showSearch
              style={{ width: 280 }}
              value={selectedTenantId || undefined}
              onChange={(value) => setSelectedTenantId(value)}
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {tenants.map((t) => (
                <Select.Option key={t.id} value={t.id}>
                  {t.name}
                </Select.Option>
              ))}
            </Select>
            <Button
              icon={<ReloadOutlined />}
              onClick={refetch}
              disabled={!selectedTenantId}
            >
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      {selectedTenantId && stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Syncs Today"
                value={stats.totalSyncsToday}
                prefix={<SyncOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Success Rate"
                value={stats.successRate}
                suffix="%"
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: stats.successRate >= 90 ? '#52c41a' : '#faad14' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Failed Jobs"
                value={stats.failedJobs}
                prefix={<CloseCircleOutlined />}
                valueStyle={{ color: stats.failedJobs > 0 ? '#f5222d' : '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Avg Duration"
                value={stats.avgDuration}
                suffix="s"
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      {!selectedTenantId && (
        <Card style={{ textAlign: 'center', padding: '60px 0' }}>
          <Text type="secondary">Please select a tenant to view sync jobs</Text>
        </Card>
      )}

      {selectedTenantId && error && (
        <Alert
          message="Lỗi tải dữ liệu"
          description={error.message || 'Vui lòng thử lại sau'}
          type="error"
          showIcon
          action={<Button onClick={refetch}>Thử lại</Button>}
          style={{ marginBottom: 24 }}
        />
      )}

      {selectedTenantId && loading && (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
        </div>
      )}

      {selectedTenantId && !loading && (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            {jobs.map((job) => {
              const badgeInfo = statusBadgeMap[job.status] || statusBadgeMap.never;
              return (
                <Col span={8} key={job.jobType}>
                  <Card
                    hoverable
                    size="small"
                    onClick={() => navigate(`/misa/${selectedTenantId}/${job.jobType}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Row justify="space-between" align="middle">
                        <Col>
                          <Text strong>
                            {JOB_LABELS[job.jobType] || job.jobType}
                          </Text>
                        </Col>
                        <Col>
                          <Badge status={badgeInfo.status} text={badgeInfo.text} />
                        </Col>
                      </Row>
                      <Row justify="space-between">
                        <Col>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {job.lastRun
                              ? dayjs(job.lastRun).fromNow()
                              : 'Never run'}
                          </Text>
                        </Col>
                        <Col>
                          {job.duration !== undefined && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {job.duration}s
                            </Text>
                          )}
                        </Col>
                      </Row>
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>

          {queueStats && (
            <Card title="Queue Stats" size="small">
              <Row gutter={16}>
                <Col span={4}>
                  <Statistic title="Waiting" value={queueStats.waiting} />
                </Col>
                <Col span={4}>
                  <Statistic title="Active" value={queueStats.active} valueStyle={{ color: '#1890ff' }} />
                </Col>
                <Col span={4}>
                  <Statistic title="Completed" value={queueStats.completed} valueStyle={{ color: '#52c41a' }} />
                </Col>
                <Col span={4}>
                  <Statistic title="Failed" value={queueStats.failed} valueStyle={{ color: '#f5222d' }} />
                </Col>
                <Col span={4}>
                  <Statistic title="Delayed" value={queueStats.delayed} valueStyle={{ color: '#faad14' }} />
                </Col>
              </Row>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default MisaDashboard;
